import {
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import { GoApplicationPurposeSelectionComponent } from '../shared/components/go-application-purpose-selection/go-application-purpose-selection.component';
import { GoFrequencyResultsResponse } from './models/go-frequency-results-response';
import { GoApplicationService } from '../shared/services/go-application/go-application.service';
import { ApplicationPageFacadeService } from '../../direct-entry/shared/services/application-page-facade/application-page-facade.service';
import { ApplicationState } from '../../../../core/models/application-state/application-state';
import {
    Observable,
    distinctUntilChanged,
    map,
    of,
    startWith,
    Subject,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';
import { GoSearchInformationComponent } from './components/go-search-information/go-search-information.component';
import { GoSearchInformationService } from './services/go-search-information-form/go-search-information.service';
import { AgGridAngular } from 'ag-grid-angular';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
} from 'ag-grid-community';
import { GridHelper } from '../../../../core/models/grid/grid-helper';
import { GoSubmitToEwaRequest } from './models/go-submit-to-ewa-request';
import { GoFrequencyPairingRequest } from './models/go-frequency-pairing-request';
import {
    FormGroup,
    FormArray,
    FormsModule,
    ReactiveFormsModule,
    FormControl,
} from '@angular/forms';
import {
    NestedFormGroupType,
    FormGroupType,
} from '../../../../core/models/form-group-type';
import { GoFrequencyPairingFormService } from './services/go-frequency-pairing-form/go-frequency-pairing-form.service';
import { GoSubmitToEwaFormService } from './services/go-submit-to-ewa-form/go-submit-to-ewa-form.service';
import { nameOf } from '../../../../core/functions/string-helpers';
import { GoFrequencyTypeCellRendererComponent } from './components/go-frequency-type-cell-renderer/go-frequency-type-cell-renderer.component';
import { GoFrequencyTypeHeaderRendererComponent } from './components/go-frequency-type-header-renderer/go-frequency-type-header-renderer.component';
import { GoApplicationPreparationService } from '../shared/services/go-application-preparation/go-application-preparation.service';
import { GoParametersSummaryComponent } from '../shared/components/go-parameters-summary/go-parameters-summary.component';
import { CheckboxCellRendererParams } from '../../../../core/models/grid/checkbox-cell-renderer-params';
import { MatInputModule } from '@angular/material/input';
import { RegexInputDirective } from '../../../../shared/directives/regex-input/regex-input.directive';
import { RegExPattern } from '../../../../core/models/regexp-pattern/regex-pattern';
import {
    MatExpansionModule,
    MatExpansionPanel,
} from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { GoFrequencyTypeRendererParams } from './components/go-frequency-type-cell-renderer/models/go-frequency-type-renderer-params';
import { GoStationClassType } from '../go-parameters/models/go-station-class-type';
import { GoManageApplicationService } from '../shared/services/go-manage-application/go-manage-application.service';
import { MatDialog } from '@angular/material/dialog';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { GoChangeInputParametersDialogMessage } from './constants/go-change-input-parameters-dialog-message';
import { RouteConstants } from '../../../../core/constants/routes';
import { ActivatedRoute, Router } from '@angular/router';
import { GoFrequencyResultsConstants } from './constants/go-frequency-results-constants';
import { DropdownOption } from '../../../../core/models/dropdown-option';
import { CreditCardSummaryService } from '../../../../core/services/commerce/credit-card-summary/credit-card-summary.service';
import { DropdownService } from '../../../../core/services/resources/dropdown.service';
import { UserStateService } from '../../../user/shared/services/user-state/user-state.service';
import { CreditCardService } from '../../../../core/services/commerce/credit-card/credit-card.service';
import { GetAllCardsOnFileResponse } from '../../../../core/models/commerce/cards-on-file/get-all-cards-on-file-response';
import { CreditCardSummary } from '../../../../core/models/commerce/cards-on-file/credit-card-summary';
import { PreferredPaymentMethodFormService } from '../../orders/order-details/services/preferred-payment-method-form.service';
import { PaymentMethod } from '../../../../core/enums/commerce/payment/payment-method';
import { MatRadioModule } from '@angular/material/radio';
import { HoverTooltipComponent } from '../../../../shared/components/tooltips/hover-tooltip/hover-tooltip.component';
import { OrderPaymentConstants } from '../../orders/shared/constants/order-payment-constants';
import { MatSelectModule } from '@angular/material/select';
import { CardTypeIconComponent } from '../../../../shared/components/card-type-icon/card-type-icon.component';
import { CardOnFileValidationMessages } from '../../orders/shared/constants/card-on-file-validation-messages';
import { GoSegmentedToggleOption } from '../go-parameters/components/go-segmented-toggle/models/go-segmented-toggle-option';
import { GoSegmentedToggleComponent } from '../go-parameters/components/go-segmented-toggle/go-segmented-toggle.component';
import { GoCardOnFileConstants } from './constants/go-card-on-file-constants';
import { PaymentSourceFormModel } from '../../orders/shared/models/payment-source-form-model';
import { ExternalLinks } from '../../../../core/constants/external-links';
import { GoPriceFrequencyPair } from './models/go-price-frequency-pair';
import { GoSubmitToEwaFormModel } from './models/go-submit-to-ewa-form-model';
import { GoFrequencyResultMessages } from './constants/go-frequency-result-messages';

@Component({
    selector: 'app-go-frequency-results',
    imports: [
        GoApplicationPurposeSelectionComponent,
        GoSearchInformationComponent,
        AgGridAngular,
        GoParametersSummaryComponent,
        MatInputModule,
        RegexInputDirective,
        MatExpansionModule,
        MatCheckboxModule,
        FormsModule,
        ReactiveFormsModule,
        MatRadioModule,
        HoverTooltipComponent,
        MatSelectModule,
        CardTypeIconComponent,
        GoSegmentedToggleComponent,
    ],
    templateUrl: './go-frequency-results.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['../shared/styles/go-mat-panel.scss'],
})
export class GoFrequencyResultsComponent implements OnInit, OnDestroy {
    @ViewChild('frequencyResultsExpansionPanel')
    frequencyResultsExpansionPanel!: MatExpansionPanel;

    goFrequencyResultsResponse: GoFrequencyResultsResponse | null = null;
    totalFees: number = 0;

    applicationState: ApplicationState | null = null;

    hasChanges: boolean = false;
    resultsExpired: boolean = false;

    regExPattern = RegExPattern;
    orderPaymentConstants = OrderPaymentConstants;

    readonly externalLinks = ExternalLinks;

    private readonly dialog = inject(MatDialog);
    private readonly router = inject(Router);
    private readonly goApplicationService = inject(GoApplicationService);
    private readonly applicationPageFacadeService = inject(
        ApplicationPageFacadeService,
    );
    private readonly goSearchInformationService = inject(
        GoSearchInformationService,
    );
    private readonly goSubmitToEwaFormService = inject(
        GoSubmitToEwaFormService,
    );
    private readonly goFrequencyPairingFormService = inject(
        GoFrequencyPairingFormService,
    );
    private readonly goApplicationPreparationService = inject(
        GoApplicationPreparationService,
    );
    private readonly goManageApplicationService = inject(
        GoManageApplicationService,
    );
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly creditCardSummaryService = inject(
        CreditCardSummaryService,
    );
    private readonly dropdownService = inject(DropdownService);
    private readonly userStateService = inject(UserStateService);
    private readonly creditCardService = inject(CreditCardService);
    private readonly preferredPaymentMethodFormService = inject(
        PreferredPaymentMethodFormService,
    );

    /**
     * Main component form used for submitting Go order to EWA
     */
    form: FormGroup<NestedFormGroupType<GoSubmitToEwaFormModel>> =
        this.goSubmitToEwaFormService.getFormDefinition();

    /**
     * Form for displaying technical data summary
     */
    technicalDataForm =
        this.goApplicationPreparationService.getFormDefinition();

    /**
     * The user's saved credit cards, used to populate the cards on file
     * dropdown and pre-select the default card if applicable.
     */
    cardsOnFile: CreditCardSummary[] = [];

    /**
     * Dropdown options for the cards on file field in the preferred payment method form.
     */
    cardsOnFileDropdownOptions: DropdownOption[] = [];

    paymentMethodSegmentedToggleOptions: GoSegmentedToggleOption<PaymentMethod>[] =
        [
            {
                id: PaymentMethod.AuthNetAim.toString(),
                label: 'Credit Card',
                value: PaymentMethod.AuthNetAim,
            },
            {
                id: PaymentMethod.InvoiceNet30.toString(),
                label: 'Invoice Net 30 (Approval Req)',
                value: PaymentMethod.InvoiceNet30,
                isDisabled: () => this.isApprovedForInvoiceNet30() === false,
            },
        ];

    rowData: GoFrequencyPairingRequest[] = [];
    gridApi: GridApi | undefined;
    columnDefs: ColDef[] = [
        {
            ...GridHelper.selectRowColDef,
            cellRendererParams: {
                visuallyDisabled: (_: CheckboxCellRendererParams) => {
                    return this.resultsExpired;
                },
            },
        },
        {
            headerName: 'Tx / Rx',
            valueGetter: (params) => params.data.frequencyLow,
            valueFormatter: (params) =>
                `${params.data.frequencyLow} / ${params.data.frequencyHigh}`,
            filter: false,
            maxWidth: 180,
        },
        {
            field: nameOf<GoFrequencyPairingRequest>('frequencyType'),
            cellRenderer: GoFrequencyTypeCellRendererComponent,
            cellRendererParams: (params: ICellRendererParams) => {
                const data: GoFrequencyTypeRendererParams = {
                    value: params.data.frequencyType,
                    stationClassType: this.getStationClassType(),
                };

                return data;
            },
            headerComponentParams: {
                innerHeaderComponent: GoFrequencyTypeHeaderRendererComponent,
            },
            filter: false,
        },
    ];

    gridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        domLayout: 'autoHeight',
        pagination: false,
        ...GridHelper.getFormCellRendererOnCellFocusedGridOptions(),
    };

    frequencyResultMessages = GoFrequencyResultMessages;

    private destroy$ = new Subject<void>();

    constructor() {}

    get frequencyPairingsFormArray(): FormArray<
        FormGroup<FormGroupType<GoFrequencyPairingRequest>>
    > {
        return this.form.controls.frequencyPairings as FormArray<
            FormGroup<FormGroupType<GoFrequencyPairingRequest>>
        >;
    }

    get commentsControl(): FormControl<string> {
        return this.form.controls.comments as FormControl<string>;
    }

    get informationIsVerifiedControl(): FormControl<boolean> {
        return this.form.controls.informationIsVerified as FormControl<boolean>;
    }

    get paymentMethodControl(): FormControl<PaymentMethod | null> {
        return this.form.controls
            .paymentMethod as FormControl<PaymentMethod | null>;
    }

    get cardOnFileIdControl(): FormControl<number | null> {
        return this.form.controls.cardOnFileId as FormControl<number | null>;
    }

    isApprovedForInvoiceNet30(): boolean {
        return this.goFrequencyResultsResponse?.isCreditApproved ?? false;
    }

    isAuthNetAim(paymentMethod: PaymentMethod | null): boolean {
        return paymentMethod === PaymentMethod.AuthNetAim;
    }

    getStationClassType(): GoStationClassType {
        const originalRequest =
            this.goFrequencyResultsResponse?.originalRequest;

        if (!originalRequest || !originalRequest.stationClassType) {
            throw new Error('Original request or station class type is null');
        }

        return originalRequest.stationClassType;
    }

    isFrequencyResultsSectionInvalid(): boolean {
        return (
            !this.resultsExpired &&
            this.frequencyPairingsFormArray.invalid &&
            (this.frequencyPairingsFormArray.dirty ||
                this.frequencyPairingsFormArray.touched)
        );
    }

    frequencyResultsSectionDescription(): string {
        const selectedCount = this.frequencyPairingsFormArray.controls.filter(
            (control) => control.controls.isSelected.value === true,
        ).length;

        return `${selectedCount} selected`;
    }

    isPaymentMethodSectionInvalid(): boolean {
        return (
            (this.paymentMethodControl.invalid &&
                (this.paymentMethodControl.dirty ||
                    this.paymentMethodControl.touched)) ||
            (this.cardOnFileIdControl.touched &&
                this.isAuthNetAim(this.paymentMethodControl.value) &&
                this.cardOnFileIdControl.invalid)
        );
    }

    cancelSearch(): void {
        const applicationId = this.applicationState?.applicationId;

        if (!applicationId) {
            throw new Error('Application ID is required to cancel the search.');
        }

        this.goManageApplicationService.cancelSearch(applicationId.toString());
        this.applicationPageFacadeService.applicationStateService.clearApplicationState();
    }

    ngOnDestroy(): void {
        this.goSubmitToEwaFormService.clearFormInvalidFn();

        this.destroy$.next();
        this.destroy$.complete();
    }

    private handleFormValidityChange(): void {
        this.form.statusChanges
            .pipe(startWith(this.form.status), takeUntil(this.destroy$))
            .subscribe(() => {
                // Whenever the form value changes, re-evaluate the disabled state by emitting from the form service
                this.goSubmitToEwaFormService.setFormInvalidFn(
                    () => this.form.invalid,
                );
            });
    }

    ngOnInit(): void {
        this.handleFormValidityChange();

        this.applicationState =
            this.applicationPageFacadeService.applicationStateService.getCurrentApplicationState();

        this.guardAgainstNullApplicationState();

        this.loadForm();
        this.handleNextClick();
        this.handleFormDirtying();
        this.handleOnResultsExpired();
        this.loadCardsOnFileAndApplyDefaults().subscribe();

        this.refreshPaymentMethodValidatorsOnSelectionChange();

        // Re-initialize the GoSearchInformationService if the applicationId is stale
        if (
            this.goSearchInformationService.getApplicationCoordinationStatus()
                ?.applicationId !== this.applicationState!.applicationId
        ) {
            this.goSearchInformationService.initialize();
        }

        // This is after instantiation logic to ensure the information service has been instantiated and the resultsExpired flag is properly set
        if (
            this.activatedRoute.snapshot.queryParamMap.get(
                GoFrequencyResultsConstants.expireRedirectQueryParam,
            ) === 'true' &&
            this.resultsExpired
        ) {
            // For users returning from an email link with this query parameter, if the results are expired, redirect to the purpose of filing page
            this.router.navigate([
                RouteConstants.Go.getFullRoute('purposeOfFiling'),
            ]);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initializeGridContext(): any {
        return {
            formArray: this.frequencyPairingsFormArray,
        };
    }

    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.updateGrid();
    }

    onChangeInputParameters(): void {
        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: GoChangeInputParametersDialogMessage.modalTitle,
                content: GoChangeInputParametersDialogMessage.modalContent,
                primaryAction: {
                    title: GoChangeInputParametersDialogMessage.primaryActionTitle,
                    action: () => {
                        this.navigateToParametersPage();
                    },
                },
            },
        });
    }

    getCardSelectionError(): string | undefined {
        const errors = this.form.controls.cardOnFileId.errors;

        if (!errors) {
            return undefined;
        }

        return errors[
            nameOf<typeof CardOnFileValidationMessages>('creditCardExpired')
        ] as string | undefined;
    }

    get selectedCardOnFile(): CreditCardSummary | null {
        return (
            this.cardsOnFile.find(
                (card) => card.id === this.form.controls.cardOnFileId.value,
            ) ?? null
        );
    }

    private navigateToParametersPage(): void {
        const applicationId = this.applicationState!.applicationId.toString();
        this.router.navigate([
            RouteConstants.Go.getFullRouteWithApplicationId(
                'parametersWithApplicationId',
                applicationId,
            ),
        ]);
    }

    private updateGrid(): void {
        this.gridApi?.setGridOption('rowData', this.rowData);
    }

    private handleNextClick(): void {
        this.applicationPageFacadeService.stepNavigationService.nextClick$
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.handleFormSubmit();
                }),
            )
            .subscribe();
    }

    private handleFormDirtying(): void {
        this.applicationPageFacadeService.formEntryService.handleFormDirtying<GoSubmitToEwaFormModel>(
            this.form,
            this.destroy$,
            (newValue: boolean) => (this.hasChanges = newValue),
        );
    }

    private loadCardsOnFileAndApplyDefaults(): Observable<void> {
        return this.loadCardsOnFile().pipe(
            tap(() => {
                const paymentMethodForm =
                    this.preferredPaymentMethodFormService.getFormDefinition();

                this.preferredPaymentMethodFormService.selectDefaultCard(
                    paymentMethodForm,
                    () => this.cardsOnFile,
                );

                if (this.form.controls.cardOnFileId.value === null) {
                    this.cardOnFileIdControl.patchValue(
                        paymentMethodForm.controls.cardOnFileId.value,
                        { emitEvent: false },
                    );
                }
            }),
            tap(() => {
                this.setupCardsOnFileDropdownOptions();
            }),
        );
    }

    private refreshPaymentMethodValidatorsOnSelectionChange(): void {
        this.paymentMethodControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    const form =
                        this.preferredPaymentMethodFormService.getFormDefinition();

                    form.controls.paymentMethod.patchValue(
                        this.paymentMethodControl.value,
                        { emitEvent: false },
                    );

                    this.goSubmitToEwaFormService.refreshAndApplyPaymentMethodValidators(
                        this.form as unknown as FormGroup<
                            FormGroupType<PaymentSourceFormModel>
                        >,
                        () => this.cardsOnFile,
                    );

                    this.form.controls.cardOnFileId.markAsTouched();
                }),
            )
            .subscribe();
    }

    private loadCardsOnFile(): Observable<void> {
        const authenticatedUser = this.userStateService.getUser();
        if (!authenticatedUser) {
            throw new Error('User is not authenticated');
        }

        return this.creditCardService.getAll(authenticatedUser.id).pipe(
            takeUntil(this.destroy$),
            tap((response: GetAllCardsOnFileResponse) => {
                // Storing the user's saved credit cards to be used for populating the cards on file
                // dropdown and for form validation (e.g., checking if the selected card is expired).
                this.cardsOnFile = response.creditCards;
            }),
            map(() => void 0),
        );
    }

    private setupCardsOnFileDropdownOptions(): void {
        const orderedCreditCards = this.creditCardSummaryService
            .orderCreditCards(this.cardsOnFile)
            .map((card) => ({
                ...card,
                isPreferred: false, // Preferred card logic is not valid for Go apps, so setting this to false for all cards to avoid confusion
            }));

        this.cardsOnFileDropdownOptions =
            this.dropdownService.transformEligibleCardsForOrderToDropdownOptions(
                orderedCreditCards,
                this.creditCardSummaryService.isExpired,
            );

        this.cardsOnFileDropdownOptions.push({
            label: 'New Card (EWA to contact me)',
            value: GoCardOnFileConstants.GoNewCardOnFileValue,
        });
    }

    private loadForm(): void {
        const applicationId = this.applicationState!.applicationId.toString()!;

        this.goApplicationService
            .getFrequencyResults(applicationId)
            .pipe(
                takeUntil(this.destroy$),
                tap((response: GoFrequencyResultsResponse) => {
                    this.goFrequencyResultsResponse = response;

                    this.goApplicationPreparationService.patchValue(
                        this.technicalDataForm,
                        response.originalRequest,
                    );

                    this.initializeFormWithResponse();

                    this.handleFrequencyPairingsTotalFeesChange();
                }),
            )
            .subscribe();
    }

    private mapFrequencyPairings(
        value: GoFrequencyPairingRequest[],
    ): GoPriceFrequencyPair[] {
        return value
            .filter((pair) => pair.isSelected === true)
            .map((pair) => ({
                minFrequencyRequestId: pair.minFrequencyRequestId!,
                maxFrequencyRequestId: pair.maxFrequencyRequestId!,
                minFrequencyResponseId: pair.minFrequencyResponseId!,
                maxFrequencyResponseId: pair.maxFrequencyResponseId!,
            }));
    }

    private handleFrequencyPairingsTotalFeesChange(): void {
        this.frequencyPairingsFormArray.valueChanges
            .pipe(
                startWith(this.frequencyPairingsFormArray.value),
                map((value) =>
                    this.mapFrequencyPairings(
                        value as GoFrequencyPairingRequest[],
                    ),
                ),
                distinctUntilChanged(
                    (a, b) => JSON.stringify(a) === JSON.stringify(b),
                ),
                switchMap((includedFrequencyPairs) => {
                    if (includedFrequencyPairs.length === 0) {
                        return of(0);
                    }

                    const applicationId =
                        this.applicationState?.applicationId.toString();

                    return this.goApplicationService
                        .calculatePrice(applicationId!, {
                            includedFrequencyPairs,
                        })
                        .pipe(map((response) => response.totalPrice));
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((totalPrice) => {
                this.totalFees = totalPrice;
            });
    }

    private initializeFormWithResponse(): void {
        if (!this.goFrequencyResultsResponse) {
            throw new Error(
                'GoFrequencyResultsResponse is null, cannot initialize form',
            );
        }

        this.goFrequencyResultsResponse.frequencyPairings.forEach(() => {
            this.frequencyPairingsFormArray.push(
                this.goFrequencyPairingFormService.getFrequencyDefinition(),
            );
        });

        this.goFrequencyPairingFormService.patchFormValue(
            this.frequencyPairingsFormArray,
            this.goFrequencyResultsResponse.frequencyPairings,
        );

        // By default, select all frequency pairings
        this.frequencyPairingsFormArray.controls.forEach((control) => {
            control.controls.isSelected.setValue(!this.resultsExpired);
        });

        this.rowData = this.frequencyPairingsFormArray
            .value as GoFrequencyPairingRequest[];

        this.updateGrid();
    }

    private handleOnResultsExpired(): void {
        this.goSearchInformationService
            .onResultsExpired(() => {
                this.resultsExpiredCallback();
            })
            .subscribe();
    }

    private resultsExpiredCallback(): void {
        this.resultsExpired = true;
        this.commentsControl.disable();
        this.informationIsVerifiedControl.disable();
        this.updateGrid();
    }

    private handleFormSubmit(): void {
        if (this.resultsExpired) {
            this.reRunExpiredApplication();

            return;
        }

        if (!this.form.valid) {
            this.form.markAllAsTouched();

            return;
        }

        const frequencyPairs = this.mapFrequencyPairings(
            this.frequencyPairingsFormArray
                .value as GoFrequencyPairingRequest[],
        );

        const cardOnFileIdValue =
            this.cardOnFileIdControl.value ===
            GoCardOnFileConstants.GoNewCardOnFileValue
                ? null
                : this.cardOnFileIdControl.value;

        const request: GoSubmitToEwaRequest = {
            comments: this.commentsControl.value,
            includedFrequencyPairs: frequencyPairs,
            paymentMethod: this.paymentMethodControl.value,
            cardOnFileId: cardOnFileIdValue,
        };

        this.goApplicationService
            .submitToEwa(
                this.applicationState!.applicationId.toString(),
                request,
            )
            .subscribe(() => {
                this.hasChanges = false;

                this.router.navigate([
                    RouteConstants.Go.getFullRouteWithApplicationId(
                        'complete',
                        this.applicationState!.applicationId.toString(),
                    ),
                ]);
            });
    }

    private reRunExpiredApplication(): void {
        const applicationId = this.applicationState!.applicationId.toString();

        this.goApplicationService
            .reRunExpiredApplication(applicationId)
            .subscribe(() => {
                // Reset the hasChanges flag to false to prevent the user from being prompted about unsaved changes when navigating away from the page.
                this.hasChanges = false;

                this.router.navigate([
                    RouteConstants.Go.getFullRouteWithApplicationId(
                        'searchProcessing',
                        applicationId,
                    ),
                ]);
            });
    }

    private guardAgainstNullApplicationState(): void {
        if (!this.applicationState) {
            throw new Error('ApplicationState is null');
        }
    }
}
